import { createTheme } from '@mantine/core';

export const appThemeTokens = {
  surface: {
    canvas: '#F7F4EE',
    subtle: '#EEE7DB',
    mat: '#EFE8DA',
    panel: '#FFFFFF',
    raised: '#FFFEFA',
    tint: '#FFF4EE',
    darkCanvas: '#1B120B',
    darkPanel: '#24150C',
    darkroom: '#2E1B10',
    signature: '#103D2B',
  },
  text: {
    primary: '#2A190E',
    secondary: '#6B5543',
    tertiary: '#9B8873',
    inverse: '#FFFDF8',
    accent: '#B95736',
    gold: '#9C7420',
  },
  border: {
    faint: '#E8DDCC',
    subtle: '#D8C8B3',
    strong: '#B99B78',
    ink: '#1B120B',
    metal: '#D8BA67',
    focus: '#B95736',
  },
  roles: {
    primary: '#B95736',
    human: '#245B45',
    conclusion: '#9C7420',
    todo: '#9C7420',
    dispute: '#B33A3A',
  },
  status: {
    success: '#245B45',
    warning: '#9C7420',
    error: '#B33A3A',
    info: '#4A6F83',
    unread: '#B95736',
  },
  texture: {
    strandInk: 'rgba(27, 18, 11, 0.64)',
    brassGreen: '#6F8F63',
  },
} as const;

const controlDefaultProps = {
  radius: 'md',
  size: 'sm',
} as const;

const controlStyles = {
  input: {
    backgroundColor: 'var(--input-bg, var(--control-bg))',
    borderColor: 'var(--control-border)',
    borderRadius: '12px',
    minHeight: '44px',
    color: 'var(--input-color, var(--mantine-color-text))',
    boxShadow: 'none',
  },
} as const;

const textareaControlStyles = {
  input: {
    backgroundColor: 'var(--input-bg, var(--control-bg))',
    borderColor: 'var(--control-border)',
    borderRadius: '12px',
    color: 'var(--input-color, var(--mantine-color-text))',
    boxShadow: 'none',
  },
} as const;

const textareaControlClassNames = {
  input: 'locs-textarea-input',
} as const;

export const appTheme = createTheme({
  primaryColor: 'terracotta',
  primaryShade: { light: 6, dark: 4 },
  fontFamily: "'Inter', 'Noto Sans SC', 'PingFang SC', sans-serif",
  headings: {
    fontFamily: "'Plus Jakarta Sans', 'Noto Sans SC', 'PingFang SC', sans-serif",
    fontWeight: '750',
  },
  fontFamilyMonospace: "'IBM Plex Mono', ui-monospace, monospace",
  colors: {
    terracotta: [
      '#fff4ee',
      '#f9e0d1',
      '#efbea3',
      '#e39371',
      '#d8764d',
      '#c9633f',
      '#B95736',
      '#984528',
      '#74311c',
      '#4f1f12',
    ],
    forestGreen: [
      '#edf7f2',
      '#d2eadf',
      '#a4d2bc',
      '#74b694',
      '#4c9c73',
      '#33845d',
      '#245B45',
      '#1c4937',
      '#143427',
      '#0b2018',
    ],
    amber: [
      '#fff8e7',
      '#f8ebc8',
      '#ead59b',
      '#d8ba67',
      '#c6a041',
      '#b88d2d',
      '#9C7420',
      '#795916',
      '#563e0e',
      '#342506',
    ],
    sand: [
      '#fffdf8',
      '#f8f2e8',
      '#efe3d1',
      '#e1cfb6',
      '#ceb695',
      '#bda17b',
      '#a98b62',
      '#8d704a',
      '#685034',
      '#3e2e1d',
    ],
    obsidian: [
      '#f5f0e9',
      '#ded2c2',
      '#bda88f',
      '#907358',
      '#6b5138',
      '#4c3624',
      '#342316',
      '#24150C',
      '#1B120B',
      '#100C08',
    ],
  },
  defaultRadius: 'md',
  shadows: {
    xs: '0 1px 0 rgba(27, 18, 11, 0.08)',
    sm: '0 8px 18px rgba(27, 18, 11, 0.06)',
    md: '0 16px 34px rgba(27, 18, 11, 0.08)',
    lg: '0 22px 58px rgba(27, 18, 11, 0.12)',
    xl: '0 30px 82px rgba(27, 18, 11, 0.16)',
  },
  components: {
    Paper: {
      defaultProps: {
        radius: 'md',
        shadow: 'xs',
      },
      styles: {
        root: {
          borderColor: 'var(--locs-border)',
          backgroundColor: 'var(--locs-panel)',
        },
      },
    },
    Card: {
      defaultProps: {
        radius: 'md',
        shadow: 'xs',
        padding: 'xl',
      },
      styles: {
        root: {
          backgroundColor: 'var(--locs-panel)',
          borderColor: 'var(--locs-border)',
          boxShadow: 'none',
        },
      },
    },
    Button: {
      defaultProps: {
        radius: 'sm',
      },
      styles: {
        root: {
          fontWeight: 800,
          letterSpacing: '0.01em',
        },
      },
    },
    Modal: {
      defaultProps: {
        radius: 'md',
        shadow: 'lg',
      },
      styles: {
        content: {
          backgroundColor: 'var(--locs-panel)',
        },
        header: {
          backgroundColor: 'var(--locs-panel)',
        },
      },
    },
    Divider: {
      styles: {
        root: {
          borderColor: 'var(--locs-border-faint)',
        },
      },
    },
    TextInput: {
      defaultProps: controlDefaultProps,
      styles: controlStyles,
    },
    PasswordInput: {
      defaultProps: controlDefaultProps,
      styles: controlStyles,
    },
    Textarea: {
      defaultProps: controlDefaultProps,
      styles: textareaControlStyles,
      classNames: textareaControlClassNames,
    },
    Select: {
      defaultProps: controlDefaultProps,
      styles: controlStyles,
    },
    Indicator: {
      defaultProps: {
        radius: 'xl',
        size: 16,
        color: 'terracotta',
      },
    },
    ScrollArea: {
      defaultProps: {
        scrollbarSize: 8,
      },
    },
  },
});
